<template>
  <div v-if="loaded" class="table-container">
    <div v-if="!tableValues.length" class="no-result show-medium">
      {{ $t('noResults') }}
    </div>

    <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <thead class="table-header">
        <tr>
          <th v-for="data in headers" :key="data" class="table-header-item">
            {{ data }}
          </th>
        </tr>
      </thead>

      <tbody class="table-content">
        <tr
          v-for="wallet in tableValues"
          :key="wallet.id"
          class="table-line"
          :class="{
            edit: edit === wallet.company.siren,
            archived: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
          }"
          @click="edit === wallet.company.siren ? null : goTo(wallet.company)"
        >
          <td
            valign="top"
            align="left"
            class="table-item read ellipsis-3-lines"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div class="item">{{ wallet.company.name }}</div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read monitoring ellipsis"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <MonitoringType
              v-if="edit !== wallet.company.siren"
              :level="getMonitoringInfos(wallet.packageLevelPublicId).level"
              :label="getMonitoringInfos(wallet.packageLevelPublicId).label"
            />
            <div v-else class="item">
              <Dropdown
                class="priority-dropdown"
                :config="{
                  inputSize: 34,
                  active: edit === wallet.company.siren,
                  showActivatedOnly: true,
                }"
                :values="packages"
                :placeholder="''"
                :on-change="value => (monitoringLevel = value)"
                :value="monitoringLevel"
              >
                <template #placeholder="{ value, isPlaceholder }">
                  <MonitoringType v-if="!isPlaceholder" :level="value.level" :label="value.label" />
                  <div v-else class="value width grey">{{ value }}</div>
                </template>
                <template #choices="{ choice }">
                  <MonitoringType :class="{ highlight: choice === monitoringLevel }" :level="choice.level" :label="choice.label" />
                </template>
              </Dropdown>
            </div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read id"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div v-if="edit !== wallet.company.siren" class="item">
              {{ wallet.internalRef }}
            </div>
            <div v-else class="item">
              <EditorField
                class="editor"
                :placeholder="$t('addInternalId')"
                :config="{
                  inputSize: 34,
                  active: edit === wallet.company.siren,
                  showActivatedOnly: true,
                }"
                :on-change="value => (internalId = value)"
                :value="internalId"
              />
            </div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read ellipsis-3-lines"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div class="item">{{ wallet.company.address }}</div>
            <div class="item">{{ wallet.company.city }}</div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read ellipsis"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div class="item">
              <span>{{ $t('SIREN') }} :</span>
              <span>{{ wallet.company.siren || 'NA' }}</span>
            </div>
            <div class="item">
              <span>{{ $t('NIC') }} :</span>
              <span>{{ wallet.company.nic || 'NA' }}</span>
            </div>
            <div class="item">
              <span>{{ $t('RCS') }} :</span>
              <span>{{ wallet.company.rcs || 'NA' }}</span>
            </div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read ellipsis-3-lines"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div class="item">{{ wallet.company.activity }}</div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read manager"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div v-if="edit !== wallet.company.siren" class="item name">
              {{ getManagerInfos(wallet.managerPublicId).firstName }}
              {{ getManagerInfos(wallet.managerPublicId).lastName }}
            </div>
            <div v-else class="item">
              <Dropdown
                class="manager-dropdown"
                :config="{
                  inputSize: 34,
                  color: 'white',
                  active: edit === wallet.company.siren,
                }"
                :values="users"
                :placeholder="$t('manager')"
                :on-change="value => (manager = value)"
                :value="manager"
              >
                <template #placeholder="{ value, isPlaceholder }">
                  <div v-if="!isPlaceholder" class="manager">{{ value.firstName }} {{ value.lastName }}</div>
                  <div v-else class="manager grey">{{ value }}</div>
                </template>
                <template #choices="{ choice }">
                  <div class="manager white" :class="{ highlight: choice === manager }">{{ choice.firstName }} {{ choice.lastName }}</div>
                </template>
              </Dropdown>
            </div>
          </td>
          <td
            valign="top"
            align="left"
            class="table-item read manager"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div class="item name">
              {{ new Date(wallet.nextRenewalDate).toLocaleDateString('fr-Fr') }}
            </div>
          </td>

          <td
            valign="top"
            align="left"
            class="table-item read"
            :class="{
              archive: getMonitoringInfos(wallet.packageLevelPublicId).level === 0,
            }"
          >
            <div
              v-if="(!edit || edit !== wallet.company.siren) && !(getMonitoringInfos(wallet.packageLevelPublicId).level === 0 && edit !== wallet.company.siren)"
              class="item link-button grey"
              @click.stop="() => setEdit(wallet)"
            >
              {{ $t('edit') }}
            </div>
            <div
              v-if="getMonitoringInfos(wallet.packageLevelPublicId).level !== 0 && edit !== wallet.company.siren"
              class="item link-button grey"
              @click.stop="() => editCompany(wallet, true)"
            >
              {{ $t('archive') }}
            </div>
            <div
              v-if="getMonitoringInfos(wallet.packageLevelPublicId).level === 0 && edit !== wallet.company.siren"
              class="item link-button grey"
              @click.stop="() => setEdit(wallet)"
            >
              {{ $t('unarchive') }}
            </div>

            <div v-if="edit && edit === wallet.company.siren" class="item link-button" @click.stop="() => editCompany(wallet, false)">
              {{ $t('save') }}
            </div>
            <div v-if="edit && edit === wallet.company.siren" class="item link-button grey" @click.stop="() => (edit = null)">
              {{ $t('cancel') }}
            </div>
          </td>
        </tr>

        <tr v-if="!tableValues.length" class="table-line empty">
          <td class="table-item" colspan="9">
            <div class="item">
              <div v-if="loading" class="lds-ring-grey">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              {{ loading ? $t('searchInProgress') : $t('noCompanies') }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="table-container show-medium">
      <div
        v-for="wallet in tableValues"
        :key="wallet.id"
        class="table-line read"
        :class="{
          archived: !(getMonitoringInfos(wallet.packageLevelPublicId).level !== 0 && edit !== wallet.company.siren),
        }"
        @click="edit === wallet.company.siren ? null : goTo(wallet.company)"
      >
        <div class="line">
          <div class="line-content">
            <div class="item bold">{{ wallet.company.name }}</div>
          </div>
          <div class="line-content">
            <div class="item">{{ wallet.internalRef }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('monitoring') }}</div>
          </div>
          <div class="line-content monitoring">
            <div class="item">
              {{ getMonitoringInfos(wallet.packageLevelPublicId).level }}
            </div>
            <div class="item">
              {{ getMonitoringInfos(wallet.packageLevelPublicId).label }}
            </div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('headOffice') }}</div>
          </div>
          <div class="line-content">
            <div class="item">{{ wallet.company.address }}</div>
            <div class="item">{{ wallet.company.city }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('registration') }}</div>
          </div>
          <div class="line-content">
            <div class="item">SIREN : {{ wallet.company.siren }}</div>
          </div>
          <div class="line-content">
            <div class="item">NIC : {{ wallet.company.nic }}</div>
          </div>
          <div class="line-content">
            <div class="item">R.C.S : {{ wallet.company.rcs }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('activity') }}</div>
          </div>
          <div class="line-content">
            <div class="item">{{ wallet.company.activity }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('manager') }}</div>
          </div>
          <div class="line-content">
            <div class="item">
              {{ getManagerInfos(wallet.managerPublicId).firstName }}
              {{ getManagerInfos(wallet.managerPublicId).lastName }}
            </div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('nextRenewalDate') }}</div>
          </div>
          <div class="line-content">
            <div class="item">{{ wallet.nextRenewalDate }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div
              v-if="getMonitoringInfos(wallet.packageLevelPublicId).level !== 0 && edit !== wallet.company.siren"
              class="item link-button grey"
              @click.stop="() => editCompany(wallet, true)"
            >
              {{ $t('archive') }}
            </div>
            <div v-else class="item link-button grey" @click.stop="() => editCompany(wallet, false)">
              {{ $t('unarchive') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <Pagination
      v-if="tableValues && tableValues.length && config.pagination"
      :pagination="pagination"
      :set-page="setPage"
      :tableValues="values"
      :pages="pages"
      :disable-dots="disableDots"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';
import EditorField from '@/components/EditorField';
import MonitoringType from '@/components/monitoringType';
import Pagination from '@/components/Pagination';
import tableMixin from '@/components/tables/TableMixin';
import debounce from '@/helpers/debounce';

export default {
  components: { Dropdown, EditorField, Pagination, MonitoringType },
  mixins: [tableMixin],

  props: {
    config: {
      type: Object,
      required: false,
      default: () => ({ pagination: false }),
    },

    packages: {
      type: Array,
      required: true,
    },

    users: {
      type: Array,
      required: true,
    },

    filters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      headers: [
        this.$t('companyLabel'),
        this.$t('monitoring'),
        this.$t('id'),
        this.$t('headOffice'),
        this.$t('registration'),
        this.$t('activity'),
        this.$t('manager'),
        this.$t('nextRenewalDate'),
        '',
      ],
      values: [],
      edit: null,
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.searchDebounce,
        maxElement: 10,
      },
      monitoringLevel: '',
      oldMonitoringLevel: '',
      internalId: '',
      manager: '',
      loaded: false,
      loading: true,
      searchDebounce: () => null,
      debounce,
    };
  },

  computed: {
    ...mapGetters(['user']),
  },

  watch: {
    async 'filters.manager'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.monitoring'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.search'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.startRenewalDate'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.endRenewalDate'(newValue, oldValue) {
      if (newValue !== oldValue) this.setPage(0);
    },

    async 'filters.paginationNumber'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.tableMixinConfig.maxElement = newValue;
        this.pagination.maxElement = newValue;
        this.setPage(0);
      }
    },
  },

  async created() {
    this.searchDebounce = this.debounce((pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) => this.getPortfolios(pageNumber, pageSize), 300);
  },

  async mounted() {
    this.loaded = true;
  },

  methods: {
    async setEdit(wallet) {
      this.edit = wallet.company.siren;
      this.monitoringLevel = this.getMonitoringInfos(wallet.packageLevelPublicId);
      this.oldMonitoringLevel = this.getMonitoringInfos(wallet.packageLevelPublicId);
      this.internalId = wallet.internalRef;
      this.manager = this.getManagerInfos(wallet.managerPublicId);
    },

    async editCompany(wallet, isForArchive) {
      if (isForArchive) {
        const popupConfirmed = await this.$store.dispatch('setPopup', {
          popupType: 'Archive',
        });

        if (!popupConfirmed) return;
      } else if (this.oldMonitoringLevel.level !== this.monitoringLevel.level && this.monitoringLevel.level && !this.oldMonitoringLevel.level) {
        await this.$store.dispatch('setPopup', {
          popupType: 'ChangeMonitoring',
        });
      }
      const archivedLevel = this.packages.find(value => value.level === 0);
      if (isForArchive) {
        if (archivedLevel) {
          await this.$store.dispatch('patchMonitoring', {
            siren: wallet.company.siren,
            packageLevelPublicId: archivedLevel.publicId,
          });
        } else {
          alert(`Aucun package ne correspond au niveau de surveillance "Archivé"`);
        }
      } else {
        await this.$store.dispatch('patchMonitoring', {
          siren: wallet.company.siren,
          internalRef: wallet.internalRef === this.internalId ? undefined : this.internalId,
          publicManagerId: this.manager.id === wallet.managerPublicId ? undefined : this.manager.id,
          packageLevelPublicId: this.monitoringLevel.publicId === wallet.packageLevelPublicId ? undefined : this.monitoringLevel.publicId,
        });
      }
      await this.searchDebounce();
      this.edit = false;
      this.monitoringLevel = '';
      this.internalId = '';
      this.manager = '';
    },

    goTo({ siren }) {
      this.$router.push({ name: 'searchByIdEnterprise', params: { id: siren } });
    },

    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      try {
        return await this.searchDebounce(pageNumber, pageSize);
      } catch (error) {
        if (error.message === 'Canceled') {
          return Promise.resolve({ data: {}, values: [] });
        }
        return Promise.reject(error);
      }
    },

    async getPortfolios(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      let data;
      if (!this.filters.isFiltered) {
        data = await this.$store.dispatch('getPortfolio', {
          pageNumber,
          pageSize,
        });
      } else {
        data = await this.$store.dispatch('getPortfolio', {
          pageNumber,
          pageSize,
          manager: this.filters.manager,
          monitoring: this.filters.monitoring,
          search: this.filters.search,
          startRenewalDate: this.filters.startRenewalDate,
          endRenewalDate: this.filters.endRenewalDate,
        });
      }
      this.values = data.items;
      this.pagination.totalLength = data.count;
      this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
      this.loading = false;
      return { data, values: data.items };
    },

    getMonitoringInfos(packageId) {
      return this.packages.find(value => value.publicId === packageId);
    },

    getManagerInfos(managerId) {
      return this.users.find(value => value.id === managerId);
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.table .table-header .table-header-item {
  padding-left: 6px;
}

.table-line {
  cursor: pointer;
}

.table .table-content .table-line,
.table-container .table-line {
  &.empty {
    .table-item {
      background: rgba(colors.$lightgrey-color, 0.1) !important;
    }

    .table-item .item {
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: colors.$lightgrey-color;
      font-size: 14px;
      font-style: italic;

      .lds-ring-grey {
        margin-right: 24px;
      }
    }
  }

  &.archived {
    .table-item {
      background: rgba(colors.$lightgrey-color, 0.1);
      .item {
        color: #bcc8d8 !important;
      }
    }
  }

  .table-item {
    padding: 25px 6px;
    width: calc(100% / 9);

    &.ellipsis-3-lines {
      .item {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
      }
    }

    .item {
      color: colors.$black-color !important;

      &.disabled {
        color: colors.$lightgrey-color !important;
      }

      font-weight: 400;
      flex-grow: 1;
      height: fit-content;
      align-items: flex-start;
      justify-content: flex-start;

      span {
        &:not(:nth-child(2n)) {
          font-weight: 500;
        }
      }

      &.link-button {
        color: colors.$lightblue-color !important;

        &.grey {
          color: colors.$lightgrey-color !important;

          &:hover {
            color: colors.$lightblue-color !important;
          }
        }

        &:hover {
          color: colors.$blue-color;
        }

        font-weight: 400;
        font-size: 13px;

        &:nth-child(2n) {
          margin-top: 16px;
        }
      }
      .priority-dropdown,
      .manager-dropdown,
      .editor {
        height: 35px;

        .manager {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        :deep(.dropdown) {
          margin-bottom: 0;
        }

        .priority-level {
          display: flex;
          align-items: center;

          &:not(:nth-child(2n)) {
            flex-shrink: 0;
            width: 17px;
            height: 17px;
            background: colors.$red-color;
            color: colors.$white-color;
            border-radius: 50%;
            font-size: 12px;
            margin: 0 8px 0 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .value {
            color: colors.$red-color;
          }
        }

        .value {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }
  }

  &:hover {
    &:not(.edit) &:not(.empty) {
      .table-item {
        cursor: pointer;
        background: rgba(colors.$lightgrey-color, 0.2);
      }
    }
  }

  &.edit {
    .table-item {
      background: colors.$unread-table-color;
      border-top: 1px solid colors.$lightblue-color;
      border-bottom: 1px solid colors.$lightblue-color;

      &:first-child {
        border: 1px solid colors.$lightblue-color;
        border-right: 0;
      }
      &:last-child {
        border: 1px solid colors.$lightblue-color;
        border-left: 0;
      }
    }
    &:hover {
      background: rgba(colors.$unread-table-color, 0.7);
    }
  }
}

.table .table-content .table-line .table-item,
.table-container .table-line .line .line-content {
  &.ellipsis .item span:nth-child(2n) {
    max-width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.monitoring {
    .item {
      &:nth-child(2n) {
        color: colors.$red-color !important;
        font-size: 14px;
      }

      &:not(:nth-child(2n)) {
        flex-shrink: 0;
        height: 17px;
        background: colors.$red-color;
        color: colors.$white-color !important;
        border-radius: 50%;
        font-size: 12px;
        margin: 0 8px 0 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  &.edit {
    .item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 8px;
      font-size: 12px;
      background: colors.$grey-color;
      border-radius: 4px;
      color: colors.$white-color;
    }
  }

  &.read-indication {
    .item {
      position: relative;
      padding-left: 12px;

      &:nth-child(2n + 1):before {
        position: absolute;
        left: 0px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        content: '';
        display: flex;
        align-items: center;
        justify-content: center;
        background: colors.$green-color;
      }
    }

    &.read .item:nth-child(2n + 1):before {
      width: 5px;
      height: 5px;
      border: 1px solid colors.$grey-color;
      background: colors.$read-table-color;
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .no-result {
    text-align: center;
    height: 256px;
    justify-content: center;
    align-items: center;
    display: flex;

    color: colors.$lightgrey-color;
    font-weight: 300;
  }

  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      &.archived {
        opacity: 0.6;
      }

      &.read {
        background: rgba(colors.$lightgrey-color, 0.2);

        .item {
          color: colors.$grey-color;
        }
      }

      .line {
        display: flex;
        align-items: flex-start;
        justify-content: space-evenly;
        flex-direction: column;
        flex-grow: 1;
        margin: 12px 0;

        &.right {
          justify-content: flex-end;
        }

        .line-content {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .item {
            display: flex;
            align-items: center;

            font-size: 13px !important;
            line-height: 20px !important;
            color: colors.$black-color !important;

            &.title {
              font-weight: 500 !important;
              font-size: 14px !important;
              color: colors.$grey-color !important;
              margin-bottom: 6px;
            }

            &.bold {
              font-weight: 500 !important;
              font-size: 15px !important;
              margin-bottom: 6px;
            }
          }

          &.read-indication {
            align-self: flex-end;
          }

          &.label,
          &.date {
            font-weight: 500;
            color: colors.$black-color;
          }

          &.date,
          .internal-id {
            font-size: 12px;
          }

          &.label {
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style>
